/**
 * Typed app analytics contract for feature `002-app-analytics`.
 * Authoritative map: specs/002-app-analytics/contracts/event-map.md
 */

export type AppAnalyticsPlatform = 'android' | 'ios' | 'web' | 'electron';

export type AppAnalyticsContext = {
  platform: AppAnalyticsPlatform;
  role?: string;
  location_type?: 'real' | 'test';
};

export type AppAnalyticsUserProperties = {
  user_id: string;
  owner_id?: string;
  name?: string;
  phone_number?: string;
  email?: string;
  role?: string;
  location_id?: string;
  location_name?: string;
  location_type?: 'real' | 'test';
  business_name?: string;
};

export type ReportId =
  | 'SALES_REPORT'
  | 'DAILY_STOCKS'
  | 'MONTHLY_SALES_REPORT'
  | 'YEARLY_SALES_TOTALS'
  | 'UPDATED_PRODUCTS'
  | 'BEST_SELLING_PRODUCTS'
  | 'EMPLOYEES';

export type ErrorProps = {
  error_category?:
    | 'network'
    | 'validation'
    | 'duplicate'
    | 'authentication'
    | 'plan_limit'
    | 'server'
    | 'unknown';
};

export type AmountProps = {
  item_count?: number;
  amount_paid?: number;
  order_total?: number;
  amount_due?: number;
};

export type PaymentProps = {
  payment_method?: 'cash' | 'mpesa' | 'pay_later';
  mpesa_mode?: 'stk' | 'paid_to_till';
  flow?: 'standard' | 'partial';
};

export type CountAfter = { count_after?: number };

export type PurchaseOrderStatusProp =
  | 'draft'
  | 'sent'
  | 'partially_fulfilled'
  | 'fulfilled'
  | 'cancelled';

export type PoSendChannel = 'email' | 'sms';

export type AppScreenProperties = AppAnalyticsContext & {
  screen_name: string;
  report_id?: ReportId;
  order_id?: string;
  product_id?: string;
  po_id?: string;
  employee_id?: string;
};

/** Auto-prompt sources (maps from ReviewService action types). */
export type ReviewPromptSource =
  | 'sell_completed'
  | 'order_saved'
  | 'report_downloaded'
  | 'po_sent'
  | 'employee_password_reset'
  | 'employee_reactivated'
  | 'product_transferred'
  | 'mpesa_connected'
  | 'product_updated';

/** All review funnel sources including sidebar CTA. */
export type ReviewSource = ReviewPromptSource | 'sidebar';

export type AppAnalyticsEventMap = {
  acq_welcome_cta_tapped: AppAnalyticsContext & {
    cta: 'new_user' | 'existing_user';
  };
  acq_create_account_tapped: AppAnalyticsContext & {
    source: 'carousel' | 'login' | 'demo_skip';
  };
  acq_register_submit_attempted: AppAnalyticsContext;
  acq_register_succeeded: AppAnalyticsContext;
  acq_register_failed: AppAnalyticsContext & ErrorProps;
  acq_otp_verify_succeeded: AppAnalyticsContext;
  acq_otp_verify_failed: AppAnalyticsContext & ErrorProps;
  acq_otp_resend_tapped: AppAnalyticsContext;
  acq_business_details_succeeded: AppAnalyticsContext;
  acq_business_details_failed: AppAnalyticsContext & ErrorProps;

  demo_started: AppAnalyticsContext;
  demo_skipped: AppAnalyticsContext;
  demo_quit: AppAnalyticsContext;
  demo_checkout_completed: AppAnalyticsContext & {
    payment_method: 'cash' | 'mpesa';
  };
  demo_finished: AppAnalyticsContext;

  commerce_checkout_started: AppAnalyticsContext &
    AmountProps & { source: 'cart' | 'order_details' };
  commerce_pay_later_saved: AppAnalyticsContext &
    AmountProps & { variant: 'pay_later' | 'send_to_cashier' };
  commerce_checkout_completed: AppAnalyticsContext &
    AmountProps &
    PaymentProps & { order_id?: string };
  commerce_partial_payment_completed: AppAnalyticsContext &
    AmountProps &
    PaymentProps & { order_id?: string };
  commerce_checkout_cancelled: AppAnalyticsContext & {
    reason: 'header_back' | 'hardware_back';
  };

  auth_login_succeeded: AppAnalyticsContext;
  auth_login_failed: AppAnalyticsContext & ErrorProps;
  auth_reset_passcode_completed: AppAnalyticsContext;
  intro_dismissed: AppAnalyticsContext & {
    intro: 'service_display' | 'scanner' | 'add_product_web';
    dont_show_again: boolean;
  };
  tutorial_opened: AppAnalyticsContext & {
    tutorial:
      | 'simple_item'
      | 'simple_unit'
      | 'variant_item'
      | 'variant_unit';
  };
  tutorial_switched: AppAnalyticsContext & {
    from:
      | 'simple_item'
      | 'simple_unit'
      | 'variant_item'
      | 'variant_unit';
    to:
      | 'simple_item'
      | 'simple_unit'
      | 'variant_item'
      | 'variant_unit';
  };
  tutorial_closed: AppAnalyticsContext & {
    tutorial:
      | 'simple_item'
      | 'simple_unit'
      | 'variant_item'
      | 'variant_unit';
    watch_duration_ms?: number;
  };
  getting_started_action_tapped: AppAnalyticsContext & {
    action: 'replay' | 'show_home' | 'switch_location';
  };

  catalog_product_created: AppAnalyticsContext & {
    source: 'pos' | 'backoffice';
    has_variants?: boolean;
    variant_count?: number;
  } & CountAfter;
  catalog_product_updated: AppAnalyticsContext & {
    source: 'pos' | 'backoffice';
  };
  catalog_product_deleted: AppAnalyticsContext & {
    source: 'backoffice';
  } & CountAfter;
  catalog_wastage_recorded: AppAnalyticsContext;
  catalog_product_transferred: AppAnalyticsContext;
  product_import_email_cta_clicked: AppAnalyticsContext & {
    source: 'pos' | 'backoffice';
  };

  staff_employee_created: AppAnalyticsContext & {
    employee_role?: string;
  } & CountAfter;
  staff_employee_updated: AppAnalyticsContext & { employee_role?: string };
  staff_employee_deleted: AppAnalyticsContext & CountAfter;
  staff_employee_status_toggled: AppAnalyticsContext & { active: boolean };

  procurement_po_created: AppAnalyticsContext & {
    line_item_count?: number;
    total_amount?: number;
    status?: PurchaseOrderStatusProp;
  } & CountAfter;
  procurement_po_updated: AppAnalyticsContext & {
    line_item_count?: number;
    total_amount?: number;
    status?: PurchaseOrderStatusProp;
  };
  procurement_po_sent: AppAnalyticsContext & { channel?: PoSendChannel };
  procurement_po_cancelled: AppAnalyticsContext;
  procurement_gr_created: AppAnalyticsContext & {
    line_count?: number;
    create_expense?: boolean;
  };
  procurement_invoice_created: AppAnalyticsContext & {
    line_item_count?: number;
    total_amount?: number;
  };

  notes_created: AppAnalyticsContext & CountAfter;
  notes_deleted: AppAnalyticsContext & CountAfter;

  reports_report_opened: AppAnalyticsContext & { report_id: ReportId };
  app_screen_viewed: AppScreenProperties;

  review_cta_tapped: AppAnalyticsContext & { source: 'sidebar' };
  review_native_requested: AppAnalyticsContext & {
    source: ReviewSource;
    prompt_cycle: number;
    action_count: number;
  };
  review_store_modal_requested: AppAnalyticsContext & {
    source: ReviewSource;
    prompt_cycle: number;
    action_count: number;
  };
  review_modal_shown: AppAnalyticsContext & { source: ReviewSource };
  review_store_opened: AppAnalyticsContext & {
    source: ReviewSource;
    channel: 'play' | 'app_store';
  };
  review_modal_dismissed: AppAnalyticsContext & {
    source: ReviewSource;
    reason: 'later' | 'backdrop';
  };
};

export type AppAnalyticsEventName = keyof AppAnalyticsEventMap;

export interface AppAnalytics {
  track<E extends AppAnalyticsEventName>(
    event: E,
    properties: AppAnalyticsEventMap[E],
  ): void;
  identify(properties: AppAnalyticsUserProperties): void;
  screen(screenName: string, properties?: AppScreenProperties): void;
  readonly providerDeliversRemotely: boolean;
}

export type AppAnalyticsProvider = {
  logEvent(name: string, properties: Record<string, unknown>): unknown;
  setUserId?(id: string | null): unknown;
  setUserProperties?(properties: Record<string, string | null>): unknown;
};

export const APP_ANALYTICS_EVENT_NAMES: readonly AppAnalyticsEventName[] = [
  'acq_welcome_cta_tapped',
  'acq_create_account_tapped',
  'acq_register_submit_attempted',
  'acq_register_succeeded',
  'acq_register_failed',
  'acq_otp_verify_succeeded',
  'acq_otp_verify_failed',
  'acq_otp_resend_tapped',
  'acq_business_details_succeeded',
  'acq_business_details_failed',
  'demo_started',
  'demo_skipped',
  'demo_quit',
  'demo_checkout_completed',
  'demo_finished',
  'commerce_checkout_started',
  'commerce_pay_later_saved',
  'commerce_checkout_completed',
  'commerce_partial_payment_completed',
  'commerce_checkout_cancelled',
  'auth_login_succeeded',
  'auth_login_failed',
  'auth_reset_passcode_completed',
  'intro_dismissed',
  'tutorial_opened',
  'tutorial_switched',
  'tutorial_closed',
  'getting_started_action_tapped',
  'catalog_product_created',
  'catalog_product_updated',
  'catalog_product_deleted',
  'catalog_wastage_recorded',
  'catalog_product_transferred',
  'product_import_email_cta_clicked',
  'staff_employee_created',
  'staff_employee_updated',
  'staff_employee_deleted',
  'staff_employee_status_toggled',
  'procurement_po_created',
  'procurement_po_updated',
  'procurement_po_sent',
  'procurement_po_cancelled',
  'procurement_gr_created',
  'procurement_invoice_created',
  'notes_created',
  'notes_deleted',
  'reports_report_opened',
  'app_screen_viewed',
  'review_cta_tapped',
  'review_native_requested',
  'review_modal_shown',
  'review_store_opened',
  'review_modal_dismissed',
];

export const APP_ANALYTICS_ALLOWED_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'platform',
  'role',
  'location_type',
  'cta',
  'source',
  'error_category',
  'payment_method',
  'mpesa_mode',
  'flow',
  'item_count',
  'amount_paid',
  'order_total',
  'amount_due',
  'order_id',
  'variant',
  'reason',
  'intro',
  'dont_show_again',
  'tutorial',
  'from',
  'to',
  'watch_duration_ms',
  'action',
  'has_variants',
  'variant_count',
  'count_after',
  'employee_role',
  'active',
  'line_item_count',
  'total_amount',
  'status',
  'channel',
  'line_count',
  'create_expense',
  'report_id',
  'screen_name',
  'product_id',
  'po_id',
  'employee_id',
  'prompt_cycle',
  'action_count',
]);

export const APP_ANALYTICS_IDENTIFY_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'owner_id',
  'name',
  'phone_number',
  'email',
  'role',
  'location_id',
  'location_name',
  'location_type',
  'business_name',
]);
